import React from 'react';
import { Box } from '@heycater/design-system';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';

import { cityMappingByLocale } from 'shared/helpers/cities';
import MainLayout from 'shared/layouts/MainLayout';
import { fetchRatings } from 'shared/services/trustedShops/fetchRatings';
import CTAWithBenefits from 'static_pages/components/CTAWithBenefits';
import FooterLinks from 'static_pages/components/FooterLinks';
import Hero from 'static_pages/components/Hero';
import MarketplaceHero from 'static_pages/components/MarketplaceHero';
import Faqs from 'static_pages/marketing_pages/components/Faqs';
import FindVirtualEventsButton from 'static_pages/marketing_pages/components/FindVirtualEventsButton';
import HowItWorksCTA from 'static_pages/marketing_pages/components/HowItWorksCTA';
import PartnersCTA from 'static_pages/marketing_pages/components/PartnersCTA';
import SearchLandingPageSection from 'static_pages/marketing_pages/components/SearchLandingPageSection';
import { AreaWithSidebarCTA } from 'static_pages/marketing_pages/components/SidebarCTA';
import TopCaterers from 'static_pages/marketing_pages/components/TopCaterers';
import { buildFaqMarkup } from 'static_pages/marketing_pages/helpers';
import Container from 'system/components/Container';

function SearchLandingPage({ page, ratings }) {
  const { t } = useTranslation('static_pages');
  const router = useRouter();
  const cityMapping = cityMappingByLocale(router.locale);
  const displayMarketplaceHero =
    page.cityFromSlug === cityMapping['berlin'] && !page.virtualEventPage;

  return (
    <MainLayout
      ratings={ratings}
      renderNavbarCta={
        page.virtualEventPage
          ? () => <FindVirtualEventsButton size="md" />
          : null
      }
    >
      <Head>
        <title>{page.pageTitle}</title>
        <meta name="description" content={page.metaDescription} />
        <meta property="og:title" content={page.pageTitle} />
        <meta property="og:description" content={page.metaDescription} />
        {page.faQsEnabled && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(buildFaqMarkup(page.faQs)),
            }}
          />
        )}
      </Head>

      {displayMarketplaceHero ? (
        <MarketplaceHero
          title={page.heroTitle || page.pageTitle}
          subtitle={page.heroSubtitle}
        />
      ) : (
        <Hero
          title={page.heroTitle || page.pageTitle}
          subtitle={page.heroSubtitle}
          virtualEvent={page.virtualEventPage}
          keywords={page.keywords}
          cityFromSlug={page.cityFromSlug}
          term={page.topCaterersSearchTerm}
        />
      )}

      <TopCaterers
        topCaterers={page.topCaterers}
        title={page.topCaterersTitle}
        city={page.topCaterersSearchCity}
        term={page.topCaterersSearchTerm}
        keywords={page.keywords}
      />

      <Box pt={4} pb={4} position="relative">
        <AreaWithSidebarCTA
          title={page.virtualEventPage && t('virtualEventsHero.titleAlt')}
          subtitle={page.virtualEventPage && t('virtualEventsHero.subtitle')}
          ctaText={
            page.virtualEventPage && t('common:labels.findVirtualEvents')
          }
        >
          <Container size="xs">
            <SearchLandingPageSection
              title={page.section1Header}
              image={page.section1Image}
              body={page.section1Body}
            />
            <SearchLandingPageSection
              title={page.section2Header}
              image={page.section2Image}
              body={page.section2Body}
            />
            <SearchLandingPageSection
              title={page.section3Header}
              image={page.section3Image}
              body={page.section3Body}
            />
          </Container>
        </AreaWithSidebarCTA>
        {page.faQsEnabled && (
          <Box px={5} pb={6}>
            <Faqs faqs={page.faQs} faqHeadline={page.faqSectionHeadline} />
          </Box>
        )}
      </Box>
      {!page.virtualEventPage && (
        <React.Fragment>
          <Box pt={4} pb={6}>
            <HowItWorksCTA />
          </Box>
          <PartnersCTA />
        </React.Fragment>
      )}
      <CTAWithBenefits
        containerSize="sm"
        virtualEvent={page.virtualEventPage}
        cityFromSlug={page.cityFromSlug}
      />
      <FooterLinks pb={{ xs: 2, sm: 6 }} pt={{ xs: 2, sm: 3 }} />
    </MainLayout>
  );
}

export async function getStaticProps() {
  const ratings = await fetchRatings();
  return {
    props: {
      robots: 'none',
      ratings,
    },
  };
}

export default SearchLandingPage;
